import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  ArrowForward,
  Palette,
  Image,
  Send,
  AutoAwesome,
  Info
} from '@mui/icons-material';
import { useBook } from '../../Context/BookContext';
import { useAuth } from '../../Context/AuthContext';
import { authenticatedPost, getTestImages } from '../../utils/api';
import { SnackbarState } from '../../types';

const updateBookStylingEndpoint = process.env.REACT_APP_LAMBDA_UPDATE_BOOK_STYLING;

interface StylingStageProps {
  onNext: () => void;
}

const StylingStage: React.FC<StylingStageProps> = ({ onNext }) => {
  const { selectedBook, availableImages, fetchImageCredits, refreshSelectedBook } = useBook();
  const { user, isAuthenticated } = useAuth();
  
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [testImageUrl, setTestImageUrl] = useState<string | null>(null);
  const [userFeedback, setUserFeedback] = useState<string>('');
  const [lastTestImageTimestamp, setLastTestImageTimestamp] = useState<number>(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleCloseSnackbar = (): void => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Update processing state when book is selected or changes
  useEffect(() => {
    // Only update processing state if polling is not active
    // This prevents race condition: when refreshSelectedBook() updates selectedBook during polling,
    // we don't want this effect to interfere with the polling's state management
    if (!pollingIntervalRef.current) {
      const testStatus = selectedBook?.TestImageStatus;
      if (testStatus === 'generating') {
        console.log('Book has generating status, showing indicator');
        setIsProcessing(true);
        setProcessingStatus('generating_test_image');
      }
      // Note: Polling will be resumed by the separate effect below
    }
    
    setUserFeedback('');
  }, [selectedBook]);

  // Load initial test image when a book is selected
  useEffect(() => {
    const loadInitialTestImage = async () => {
      if (!isAuthenticated || !selectedBook) return;

      try {
        const testImages = await getTestImages(selectedBook.FileId);
        
        if (testImages.length > 0) {
          const latestTestImage = testImages.reduce((latest, current) => 
            current.timestamp > latest.timestamp ? current : latest
          );

          setTestImageUrl(latestTestImage.imageUrl);
          setLastTestImageTimestamp(latestTestImage.timestamp);
        }
      } catch (error) {
        console.error('Error loading initial test image:', error);
      }
    };

    loadInitialTestImage();
  }, [isAuthenticated, selectedBook]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Stop polling function
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Start polling for book status and test image updates
  const startPolling = useCallback(() => {
    stopPolling(); // Clear any existing interval
    
    const pollUpdates = async () => {
      if (!selectedBook) return;
      
      try {
        // Refresh book to get updated status
        const updatedBook = await refreshSelectedBook();
        const testImageStatus = updatedBook?.TestImageStatus;
        
        console.log('Polling - TestImageStatus:', testImageStatus);
        
        // Handle completed status - stop polling and show success
        if (testImageStatus === 'completed') {
          console.log('Test image generation completed, stopping polling');
          setIsProcessing(false);
          setProcessingStatus('');
          stopPolling();
          
          // Refresh credits one final time
          fetchImageCredits();
          
          // Check if there's a new test image to display
          const testImages = await getTestImages(selectedBook.FileId);
          if (testImages.length > 0) {
            const latestTestImage = testImages.reduce((latest, current) => 
              current.timestamp > latest.timestamp ? current : latest
            );

            if (latestTestImage.timestamp > lastTestImageTimestamp) {
              setTestImageUrl(latestTestImage.imageUrl);
              setLastTestImageTimestamp(latestTestImage.timestamp);
            }
          }
          
          setSnackbar({
            open: true,
            message: 'Test image generated successfully!',
            severity: 'success'
          });
          return; // Exit polling function
        }
        
        // Handle error status - stop polling and show error
        if (testImageStatus === 'error') {
          console.log('Test image generation failed');
          setIsProcessing(false);
          setProcessingStatus('');
          stopPolling();
          fetchImageCredits(); // Final credit refresh
          
          setSnackbar({
            open: true,
            message: 'Test image generation failed. Please try again.',
            severity: 'error'
          });
          return;
        }
        
        // Still generating - update status and poll credits
        if (testImageStatus === 'generating') {
          setIsProcessing(true);
          setProcessingStatus('generating_test_image');
          fetchImageCredits();
        }
        
      } catch (error) {
        console.error('Error polling for updates:', error);
      }
    };

    // Poll immediately, then every 3 seconds
    pollUpdates();
    pollingIntervalRef.current = setInterval(pollUpdates, 3000);
  }, [selectedBook, refreshSelectedBook, fetchImageCredits, lastTestImageTimestamp, stopPolling]);

  // Resume polling if book is in generating state (e.g. user refreshed page during generation)
  useEffect(() => {
    if (selectedBook?.TestImageStatus === 'generating' && !pollingIntervalRef.current) {
      console.log('Resuming polling for generating test image');
      setIsProcessing(true);
      setProcessingStatus('generating_test_image');
      startPolling();
    }
  }, [selectedBook?.FileId, selectedBook?.TestImageStatus, startPolling]);

  // Update book styling and generate new test image (combined action)
  const handleUpdateStyling = async (): Promise<void> => {
    if (!isAuthenticated || !user?.userId) {
      setSnackbar({
        open: true,
        message: 'User not authenticated',
        severity: 'error'
      });
      return;
    }

    if (!selectedBook) {
      setSnackbar({
        open: true,
        message: 'Please select a book first',
        severity: 'error'
      });
      return;
    }

    if (!userFeedback.trim()) {
      setSnackbar({
        open: true,
        message: 'Please describe what you\'d like to change about the styling',
        severity: 'warning'
      });
      return;
    }

    if (availableImages !== null && availableImages === 0) {
      setSnackbar({
        open: true,
        message: 'You need at least 1 credit to generate a test image.',
        severity: 'error'
      });
      return;
    }

    if (!updateBookStylingEndpoint) {
      setSnackbar({
        open: true,
        message: 'Update styling endpoint not configured.',
        severity: 'error'
      });
      return;
    }

    setIsProcessing(true);
    setProcessingStatus('updating_style');

    try {
      const response = await authenticatedPost(updateBookStylingEndpoint!, {
        FileId: selectedBook.FileId,
        UserFeedback: userFeedback.trim()
      });
      
      console.log('Styling update response:', response.data);
      
      setSnackbar({
        open: true,
        message: 'Styling updated! Generating new test image...',
        severity: 'success'
      });
      
      setUserFeedback('');
      setProcessingStatus('generating_test_image');
      
      // Start polling for the new test image
      startPolling();
      
    } catch (error: any) {
      console.error('Error updating styling:', error);
      
      let errorMessage = 'Failed to update styling. Please try again.';
      if (error && typeof error === 'object' && 'response' in error) {
        const response = error.response;
        if (response && response.data && response.data.message) {
          errorMessage = response.data.message;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      setIsProcessing(false);
      setProcessingStatus('');
    }
  };

  // Book is ready if completed
  const isBookReady = selectedBook?.ProcessingStatus === 'completed';
  
  // Book is still processing if not completed and not in error state
  const isBookProcessing = selectedBook?.ProcessingStatus && ![
    'completed',
    'error_upload',
    'error_info',
    'error_summary',
    'error_subjects',
    'error_interesting'
  ].includes(selectedBook.ProcessingStatus);
  
  // Helper to get status display info
  const getStatusInfo = () => {
    if (processingStatus === 'updating_style') {
      return {
        text: 'Updating styling...',
        color: 'secondary.main',
        icon: <AutoAwesome sx={{ fontSize: 16 }} />
      };
    } else if (processingStatus === 'generating_test_image') {
      return {
        text: 'Generating test image...',
        color: 'primary.main',
        icon: <Image sx={{ fontSize: 16 }} />
      };
    }
    return null;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {isBookProcessing ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your book is still processing. Please wait for it to complete before adjusting styling.
        </Alert>
      ) : null}

      {/* Processing Status */}
      {isProcessing && getStatusInfo() && (
        <Box sx={{ mb: 3, width: '100%', maxWidth: '100%' }}>
          <Alert 
            severity="info" 
            icon={getStatusInfo()!.icon}
            sx={{ 
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderLeft: `4px solid ${getStatusInfo()!.color}`,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {getStatusInfo()!.text}
              </Typography>
              <CircularProgress size={16} />
            </Box>
          </Alert>
          <LinearProgress sx={{ mt: 1 }} />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Test Image Display */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            border: '2px solid',
            borderColor: 'primary.main',
            height: '100%'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Image sx={{ color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  Test Image Preview
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                This test image shows how your book's styling will look when you generate images
              </Typography>

              {/* Test Image Display */}
              <Box sx={{ 
                minHeight: 400,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.paper'
              }}>
                {testImageUrl ? (
                  <img 
                    src={testImageUrl} 
                    alt="Style preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '500px',
                      borderRadius: '8px'
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Image sx={{ color: 'text.disabled', fontSize: 64, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {isProcessing 
                        ? 'Generating your test image...' 
                        : 'Your test image will appear here after processing completes'
                      }
                    </Typography>
                    {isProcessing && (
                      <CircularProgress size={20} sx={{ mt: 1 }} />
                    )}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Styling Controls */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            border: '2px solid',
            borderColor: 'secondary.main',
            height: '100%'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Palette sx={{ color: 'secondary.main', fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  Adjust Styling
                </Typography>
              </Box>
              
              {/* Feedback Input */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
                  Describe what you'd like to change:
                </Typography>
                <TextField
                  multiline
                  rows={8}
                  value={userFeedback}
                  onChange={(e) => setUserFeedback(e.target.value)}
                  placeholder="e.g., 'Make it brighter and more colorful', 'Less cartoony, more realistic', 'Darker and more dramatic'..."
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${userFeedback.length}/100 characters`}
                  disabled={!isBookReady || isProcessing}
                />
                <Button
                  variant="contained"
                  startIcon={isProcessing ? <CircularProgress size={16} /> : <Send />}
                  onClick={handleUpdateStyling}
                  disabled={isProcessing || !userFeedback.trim() || !isBookReady || availableImages === 0}
                  fullWidth
                  sx={{
                    backgroundColor: 'secondary.main',
                    '&:hover': { 
                      backgroundColor: 'secondary.dark',
                      transform: 'translateY(-1px)'
                    },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    mb: 2
                  }}
                >
                  {isProcessing ? 'Processing...' : 'Update Styling & Generate Test Image (1 credit)'}
                </Button>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    display: 'block'
                  }}
                >
                  This will update your styling and automatically generate a new test image to preview the changes
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Next Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={onNext}
          disabled={!isBookReady}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': { 
              backgroundColor: 'primary.dark',
              transform: 'translateY(-2px)'
            },
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            px: 4
          }}
        >
          Next: Generate Images
        </Button>
      </Box>

      {/* How This Works Info Box */}
      <Card sx={{ 
        mt: 4,
        border: '2px solid',
        borderColor: 'info.main',
        backgroundColor: 'info.light'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Info sx={{ color: 'info.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              How This Works
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            The styling stage lets you define the visual style that all your book's images will follow—things like "colorful", "dark", "realistic", "for children", and so on. 
            Our AI suggests an initial style based on your book's content, but this might not match your expectations. 
            For example, a Harry Potter book might be styled for children by default, which might not appeal to adult readers. 
            The test image from the previous step is shown here for you to evaluate. 
            You can provide feedback describing what you'd like to change, and our system will adjust the style accordingly and generate a new test image. 
            This iterative process continues until you're satisfied with the look. 
            Each time you update the styling and generate a new test image, it costs 1 credit.
          </Typography>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StylingStage;

